import cloneDeep from 'lodash/cloneDeep.js'
import omit from 'lodash/omit.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../../store.js'
import ConfirmModal from '../../../../common/components/Modal/ConfirmModal.js'
import {
  presetSelector,
  presetsSelector,
  savePreset,
} from '../../../../data/presets.js'
import {showMessageToast} from '../../../Header/Toast/index.js'
import {
  EDIT_PRESET_LABEL_ID,
  labelConfigSelector,
  labelInfoSelector,
  setLabelInfos,
  labelShipperTypeSelector,
  validShipperIDsSelector,
  updateLabelConfig,
  removeLabelInfo,
  shipperTypesSelector,
  labelPropertiesSelector,
} from '../../../../data/labelInfos/index.js'
import ShipperSelect from '../../Fields/ShipperSelect/index.js'
import PackageType from '../../Fields/PackageType.js'
import TextInput from '../../../../common/components/TextInput.js'
import {isPresent} from '../../../../common/utils.js'
import {showCreateRuleModal} from '../../../Modals/CreateRuleModal.js'
import {
  APPLY_PRESETS,
  RETURN_PRESET,
  SHIPPING_PRESET,
} from '../../../../common/constants/Rules.js'
import {
  RETURN_TAB,
  orderActionPanelCurrentTabSelector,
} from '../../../OrderListPage/ActionPanel/orderActionPanelFunctions.js'
import SingleInsurance from '../../Fields/Insurance/SingleInsurance.js'
import ShippingMethod from '../../Fields/ShippingMethod.js'
import MultiboxButton from '../../Fields/MultiboxButton.js'
import {
  DEFAULT_LABEL_CONFIG,
  DEFAULT_PACKAGE_SHAPE,
  LABEL_TYPE_SHIPPING,
} from '../../../../common/constants/LabelConfig.js'
import OldLabelConfigForm from '../../OldLabelConfigForm.js'
import LabelConfigContext, {
  ENABLE_MODE__FULL,
} from '../../LabelConfigContext.js'
import {usePresetMultiShipperSelector} from '../../../../data/company.js'
import {AdditionalProperties, LABEL_PROPERTIES} from '../../LabelConfigForm.js'
import {formatPreferredShippingMethodsProperty} from '../../Fields/ShippingMethodFilter.js'
import ShipperOptions from '../../../../data/shipperOptions.js'
import ShippersFilter from '../../Fields/ShippersFilter.js'
import {memOutcome} from '../../../../common/mem.js'
import {getUnique} from '../../../../common/getUnique.js'

const MODAL_FORM = 'edit_preset_modal'

export function showEditPresetModal({presetID, labelInfoID} = {}) {
  const usePresetMultiShipper = usePresetMultiShipperSelector(getState())
  const preset = presetID
    ? presetSelector(getState(), {presetID})
    : {
        name: getUniquePresetName(),
        body: {
          properties: [],
          override: [],
          labelConfig: labelInfoID
            ? cloneDeep(labelConfigSelector(getState(), {labelInfoID}))
            : {
                ...cloneDeep(DEFAULT_LABEL_CONFIG),
                packages: [cloneDeep(DEFAULT_PACKAGE_SHAPE)],
              },
        },
      }

  if (!presetID && !usePresetMultiShipper) {
    // set shipper_ids to whatever the selected rate shipper_id is
    // temporary fix while we get multi-shipper presets working
    // only do this for new presets, since existing presets can be fixed in db update
    preset.body.labelConfig.shipper_ids = preset.body.labelConfig.shipper_id
      ? [preset.body.labelConfig.shipper_id]
      : []
  }

  setLabelInfos([
    {
      id: EDIT_PRESET_LABEL_ID,
      config: cloneDeep(preset.body.labelConfig),
      type: LABEL_TYPE_SHIPPING,
      properties: preset.body.properties,
      override: new Set(preset.body.override),
    },
  ])

  // set initial shipper if new from scratch (from IdlePanel)
  if (!labelInfoID && !presetID) {
    const shipperIDs = validShipperIDsSelector(getState(), {
      labelInfoID: EDIT_PRESET_LABEL_ID,
    })

    updateLabelConfig(EDIT_PRESET_LABEL_ID, {
      shipper_id: shipperIDs[0],
      shipper_ids: usePresetMultiShipper ? shipperIDs : [shipperIDs[0]],
    })

    const shipperTypes = shipperTypesSelector(getState(), {
      labelInfoID: EDIT_PRESET_LABEL_ID,
    })

    updateLabelConfig(
      EDIT_PRESET_LABEL_ID,
      shipperTypes.reduce((prev, shipperType) => {
        const options = ShipperOptions[shipperType]?.shipping_method

        if (options?.length) {
          prev[formatPreferredShippingMethodsProperty(shipperType)] = [
            options[0].value,
          ]
        }
        return prev
      }, {}),
    )
  }

  setForm(MODAL_FORM, {
    presetID,
    createRuleAfterSave: false,
    name: preset.name,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)

  removeLabelInfo(EDIT_PRESET_LABEL_ID)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

function getErrors(presetID, name, presets) {
  const errors = {}
  name = name.trim()

  if (!isPresent(name)) {
    errors.name = 'Name is required'
    errors.preventSave = true
  } else {
    if (
      Object.values(presets).find(
        (preset) => preset.id !== presetID && preset.name === name,
      )
    ) {
      errors.name = 'Preset already exists with that name'
      errors.preventSave = true
    }
  }

  return errors
}
getErrors.default = {}

export function errorsSelector(state) {
  const {presetID, name} = modalFormSelector(state)
  const presets = presetsSelector(getState())

  return errorsSelector.memFunc(presetID, name, presets)
}
errorsSelector.memFunc = memOutcome(getErrors)

const defaultNameRegEx = /^Preset (\d+)$/
function getUniquePresetName() {
  const presets = presetsSelector(getState())

  return getUnique(
    'Preset ',
    defaultNameRegEx,
    Object.values(presets).map(({name}) => name),
  )
}

export function presetParamsSelector(state) {
  const {presetID, name} = modalFormSelector(state)
  const {
    properties,
    override = new Set(),
    config,
  } = labelInfoSelector(state, {
    labelInfoID: EDIT_PRESET_LABEL_ID,
  })

  return {
    id: presetID || undefined,
    name: name.trim(),
    body: {
      version: '2',
      properties,
      override: [...override],
      labelConfig: omit(config, [
        'rates',
        'order_id',
        'label_type',
        'error_message',
        'rates_loading',
        'rates_updated',
        'departed_shipping_method',
        'additional_options_open',
        'shipping_address',
      ]),
    },
  }
}

export async function confirm() {
  try {
    updateModalForm({isSaving: true, serverError: null})

    const {presetID, createRuleAfterSave} = modalFormSelector(getState())

    const presetParams = presetParamsSelector(getState())

    const preset = await savePreset(presetParams)

    showMessageToast(presetID ? 'Updated Preset' : 'Created Preset')

    closeModal()

    if (createRuleAfterSave) {
      const currentActionPanelTab =
        orderActionPanelCurrentTabSelector(getState())

      showCreateRuleModal({
        action: [
          {
            type: APPLY_PRESETS,
            data: {
              preset_ids: [preset.id],
              preset_type:
                currentActionPanelTab === RETURN_TAB
                  ? RETURN_PRESET
                  : SHIPPING_PRESET,
            },
          },
        ],
      })
    }
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function onChange() {
  // noop
}

function OldPresetUI({form, shipperType, errors}) {
  return (
    <LabelConfigContext.Provider
      value={{
        contextID: MODAL_FORM,
        labelInfoID: EDIT_PRESET_LABEL_ID,
        onChange,
        shipperType,
        isLabelEnableMode: true,
        enableMode: ENABLE_MODE__FULL,
      }}
    >
      <div className="row">
        <div className="medium-7 columns">
          <TextInput
            className="margin-bottom-5"
            id="preset_name"
            label="Preset Name"
            required
            value={form.name}
            onChange={(name) => updateModalForm({name})}
            errorMessage={errors.name}
          />
        </div>
      </div>
      <div className="inner-wrap__panel--actions wrap--edit-preset-list">
        <ul className="list list--shipping-options flex--justify flex-wrap-margin-10">
          <ShipperSelect />
          <PackageType />
        </ul>
        <OldLabelConfigForm />
        <SingleInsurance isOld />
        <ul className="list list--shipping-options">
          <ShippingMethod />
          <MultiboxButton />
        </ul>
      </div>
      <div className="row">
        <div className="medium-12 columns">
          <div className="alert alert--standard margin-top-10">
            <label className="margin-bottom-0" htmlFor="create_rule_after_save">
              <input
                type="checkbox"
                id="create_rule_after_save"
                className="margin-right-5 margin-bottom-0"
                value={form.createRuleAfterSave}
                onChange={(e) =>
                  updateModalForm({createRuleAfterSave: e.target.checked})
                }
              />
              <span>Create a rule that automatically applies this preset</span>
            </label>
          </div>
        </div>
      </div>
    </LabelConfigContext.Provider>
  )
}

function EditPresetModal({form}) {
  const errors = useSelector(errorsSelector)
  const usePresetMultiShipper = useSelector(usePresetMultiShipperSelector)
  const shipperType = useSelector((state) =>
    labelShipperTypeSelector(state, {labelInfoID: EDIT_PRESET_LABEL_ID}),
  )
  const shipperTypes = useSelector((state) =>
    shipperTypesSelector(state, {labelInfoID: EDIT_PRESET_LABEL_ID}),
  )
  const properties = useSelector((state) =>
    labelPropertiesSelector(state, {labelInfoID: EDIT_PRESET_LABEL_ID}),
  )

  return (
    <ConfirmModal
      className="modal--edit-preset"
      title={`${form.presetID ? 'Edit' : 'Create'} Preset`}
      confirmText={form.presetID ? 'Save' : 'Create'}
      cancelText="Cancel"
      onConfirm={() => confirm()}
      onCancel={() => closeModal()}
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
    >
      {usePresetMultiShipper ? (
        <>
          <div className="wrap--input-tall margin-bottom-25 w-50">
            <TextInput
              className="margin-bottom-5"
              id="preset_name"
              label="Preset Name"
              required
              value={form.name}
              onChange={(name) => updateModalForm({name})}
              errorMessage={errors.name}
            />
          </div>
          <div className="alert alert--neutral margin-bottom-25">
            <p className="fs-00 lh-md margin-bottom-10">
              <strong>
                At least one carrier account must be selected to ensure valid
                shipping preset properties are displayed.
              </strong>
            </p>

            <LabelConfigContext.Provider
              value={{
                contextID: `${MODAL_FORM}_carrier`,
                labelInfoID: EDIT_PRESET_LABEL_ID,
                onChange,
                shipperType: null,
              }}
            >
              <ShippersFilter />
              <p className="fs-n1 lh-md margin-top-15 margin-bottom-5 op-75">
                <em>
                  Selected carrier accounts will only be applied with the preset
                  if the <strong>“Assign Carriers”</strong> property is enabled.
                </em>
              </p>
            </LabelConfigContext.Provider>
          </div>
          <div className="inner-wrap__panel--actions wrap--edit-preset-list wrap--edit-preset-list-mc">
            <ul className="list list--shipping-options">
              <LabelConfigContext.Provider
                value={{
                  contextID: MODAL_FORM,
                  labelInfoID: EDIT_PRESET_LABEL_ID,
                  onChange,
                  isLabelEnableMode: true,
                  isPickMode: true,
                }}
              >
                <AdditionalProperties
                  shipperTypes={shipperTypes}
                  properties={LABEL_PROPERTIES.preset_top_level}
                />
              </LabelConfigContext.Provider>
            </ul>
          </div>
          {properties.length > 0 && (
            <div className="alert alert--standard margin-top-30">
              <label
                className="margin-bottom-0"
                htmlFor="create_rule_after_save"
              >
                <input
                  type="checkbox"
                  id="create_rule_after_save"
                  className="margin-right-5 margin-bottom-0"
                  value={form.createRuleAfterSave}
                  onChange={(e) =>
                    updateModalForm({createRuleAfterSave: e.target.checked})
                  }
                />
                <span>
                  Create a rule that automatically applies this preset
                </span>
              </label>
            </div>
          )}
        </>
      ) : (
        <OldPresetUI form={form} shipperType={shipperType} errors={errors} />
      )}
    </ConfirmModal>
  )
}

export default onlyIfForm(EditPresetModal, modalFormSelector)
